import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuLinks, Link, LinkComponentPropsInterface } from 'swo-ciam-common-react';
import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import { RoutePathsEnum } from '@src/core/routing/route-paths.enum';
import { useQuery } from 'react-query';
import { useInjectableServices } from '@src/server/service-registry.provider';

type QuickLink = Link & LinkComponentPropsInterface;
type QuickLinksHookResult = MenuLinks;

const getTranslationKeyDisplayName = {
  Reports: LocaleTranslationKeysEnum.QuickLinkMenuItemReports,
  Tickets: LocaleTranslationKeysEnum.QuickLinkMenuItemServiceManagement,
  'Publisher updates': LocaleTranslationKeysEnum.QuickLinkMenuItemPublisherUpdates,
  Requests: LocaleTranslationKeysEnum.QuickLinkMenuItemRequests,
  Automation: LocaleTranslationKeysEnum.QuickLinkMenuItemAutomation,
  AuditLogs: LocaleTranslationKeysEnum.QuickLinkMenuItemAuditLogs,
  'My projects': LocaleTranslationKeysEnum.QuickLinkMenuItemMyProjects,
  'Cost optimization': LocaleTranslationKeysEnum.QuickLinkMenuItemCostOptimisation,
  'Cost analytics': LocaleTranslationKeysEnum.QuickLinkMenuItemCostAnalytics,
  'Share a secret': LocaleTranslationKeysEnum.QuickLinkMenuItemSecretSharing,
  'Cloud navigator': LocaleTranslationKeysEnum.QuickLinkMenuItemCloudNavigator,
  Documentation: LocaleTranslationKeysEnum.QuickLinkMenuItemDocumentation,
  Monitoring: LocaleTranslationKeysEnum.QuickLinkMenuItemMonitoring,
  Backup: LocaleTranslationKeysEnum.QuickLinkMenuItemBackup,
  '365 Analytics': LocaleTranslationKeysEnum.QuickLinkMenuItem365Analytics,
  ContactUs: LocaleTranslationKeysEnum.SwoUserInfoMenuCommonContactUs,
  LanguageSettings: LocaleTranslationKeysEnum.SwoUserInfoMenuUserLanguageSettings,
  NotificationSettings: LocaleTranslationKeysEnum.SwoUserInfoMenuUserNotificationSettings,
  ProfileSettings: LocaleTranslationKeysEnum.SwoUserInfoMenuUserProfileSettings,
  UserManagement: LocaleTranslationKeysEnum.SwoUserInfoMenuUserManagement,
};

const getTranslationKeyDescription = {
  Reports: LocaleTranslationKeysEnum.QuickLinkMenuItemReportsDescription,
  Tickets: LocaleTranslationKeysEnum.QuickLinkMenuItemServiceManagementDescription,
  'Publisher updates': LocaleTranslationKeysEnum.QuickLinkMenuItemPublisherUpdatesDescription,
  Requests: LocaleTranslationKeysEnum.QuickLinkMenuItemRequestsDescription,
  Automation: LocaleTranslationKeysEnum.QuickLinkMenuItemAutomationDescription,
  Automations: LocaleTranslationKeysEnum.QuickLinkMenuItemAutomationDescription,
  AuditLogs: LocaleTranslationKeysEnum.QuickLinkMenuItemAuditLogsDescription,
  'My projects': LocaleTranslationKeysEnum.QuickLinkMenuItemMyProjectsDescription,
  'Cost optimization': LocaleTranslationKeysEnum.QuickLinkMenuItemCostOptimisationDescription,
  'Cost analytics': LocaleTranslationKeysEnum.QuickLinkMenuItemCostAnalyticsDescription,
  'Share a secret': LocaleTranslationKeysEnum.QuickLinkMenuItemSecretSharingDescription,
  'Cloud navigator': LocaleTranslationKeysEnum.QuickLinkMenuItemCloudNavigatorDescription,
  Documentation: LocaleTranslationKeysEnum.QuickLinkMenuItemDocumentationDescription,
  Monitoring: LocaleTranslationKeysEnum.QuickLinkMenuItemMonitoringDescription,
  Backup: LocaleTranslationKeysEnum.QuickLinkMenuItemBackupDescription,
  '365 Analytics': LocaleTranslationKeysEnum.QuickLinkMenuItem365AnalyticsDescription,
  ContactUs: LocaleTranslationKeysEnum.SwoUserInfoMenuCommonContactUsDescription,
  LanguageSettings: LocaleTranslationKeysEnum.SwoUserInfoMenuUserLanguageSettingsDescription,
  NotificationSettings:
    LocaleTranslationKeysEnum.SwoUserInfoMenuUserNotificationSettingsDescription,
  ProfileSettings: LocaleTranslationKeysEnum.SwoUserInfoMenuUserProfileSettingsDescription,
  UserManagement: LocaleTranslationKeysEnum.SwoUserInfoMenuUserManagement,
};

export const useQuickLinks = (): QuickLinksHookResult => {
  const { t: translateText } = useTranslation();

  const { UserService } = useInjectableServices();

  const { data: quickLinks = [] } = useQuery('quick-links', async () =>
    UserService.getUserQuickLinks().then((data) => data)
  );

  const menuLinks = useMemo(() => {
    const getQuickLinkUrl = (link: QuickLink): string => {
      if (link.url === RoutePathsEnum.SupportSystemHome) {
        return RoutePathsEnum.CasesIndividual;
      }

      return link.url;
    };

    const transform = (link: QuickLink): Link => {
      const url = getQuickLinkUrl(link);

      return {
        id: link.id,
        url,
        displayName:
          translateText(getTranslationKeyDisplayName[link.displayName]) || link.displayName,
        description:
          translateText(getTranslationKeyDescription[link.displayName]) || link.description,
        linkType: link.linkType,
        contextType: link.contextType,
        icon: link.icon,
        isOnPage: () => window.location.pathname.includes(url),
      };
    };

    const byBusinessOrder = (
      { displayName: firstDisplayName }: Link,
      { displayName: secondDisplayName }: Link
    ): number => firstDisplayName.localeCompare(secondDisplayName);

    const processedLinks = quickLinks
      .filter((link) => Boolean(link.url))
      .map(transform)
      .sort(byBusinessOrder);

    return {
      externalLinks: [],
      internalRouteLinks: processedLinks,
      internalAppLinks: [],
    };
  }, [translateText, quickLinks]);

  return menuLinks;
};
